import React, { useMemo } from "react";
import { Card, CardHeader, Input, ListGroup, ListGroupItem, Button } from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import PropTypes from "prop-types";
import ContentLoader from "./ContentLoader";
import { useSearch } from "../../hooks/useSearch";
import { GLOBAL_STATUS_LABELS } from "../../constants/common";

/**
 * MasterSidebar
 * Componente genérico para a lista lateral em layouts Master-Detail.
 * Agora suporta Drag and Drop opcionalmente.
 */
const MasterSidebar = ({
    title,
    subtitle,
    items,
    activeId,
    onSelect,
    onCreateNew,
    loading,
    searchPlaceholder = "Buscar...",
    renderItem,
    icon = "mdi mdi-format-list-bulleted",
    onReorder,
    loadingMessage = "Carregando itens...",
    headerContent,
    hideSearch = false,
}) => {
    const statusMatcher = useMemo(() => {
        return (item, lowerSearch) => {
            if (!item.status) return false;
            // Status fallback for legacy string checks or direct enum matches
            const label = GLOBAL_STATUS_LABELS[item.status] || item.status;
            return label.toLowerCase().includes(lowerSearch);
        };
    }, []);

    const searchFields = useMemo(() => ['name', 'title', 'fullName', 'description', 'nameService'], []);
    const { searchTerm, setSearchTerm, filteredData: filteredItems } = useSearch(items, searchFields, statusMatcher);

    const isDraggable = onReorder && !searchTerm;

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        onReorder(result.source.index, result.destination.index);
    };

    const renderListContent = () => {
        if (loading && items.length === 0) {
            return (
                <div className="py-5">
                    <ContentLoader message={loadingMessage} />
                </div>
            );
        }

        if (filteredItems.length === 0) {
            return (
                <div className="text-center py-5 px-3">
                    <div className="mb-3">
                        <i className="mdi mdi-magnify-off-outline text-muted" style={{ fontSize: "3rem" }}></i>
                    </div>
                    <p className="text-muted small mb-0">
                        {searchTerm ? `Nenhum resultado para "${searchTerm}"` : "Nenhum item cadastrado."}
                    </p>
                </div>
            );
        }

        if (isDraggable) {
            return (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="sidebar-list">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {filteredItems.map((item, index) => (
                                    <Draggable key={item.id} draggableId={item.id} index={index}>
                                        {(providedSnapshot, snapshot) => (
                                            <div
                                                ref={providedSnapshot.innerRef}
                                                {...providedSnapshot.draggableProps}
                                                className={snapshot.isDragging ? "shadow-lg" : ""}
                                                style={{
                                                    ...providedSnapshot.draggableProps.style,
                                                    zIndex: snapshot.isDragging ? 1000 : 1
                                                }}
                                            >
                                                <ListGroupItem
                                                    tag="div"
                                                    action
                                                    onClick={() => onSelect(item)}
                                                    className={`py-3 px-4 d-flex align-items-center justify-content-between transition-all select-none cursor-pointer neo-sidebar-item ${activeId === item.id ? 'active' : ''}`}
                                                    style={{
                                                        borderLeft: activeId === item.id ? "4px solid var(--color-brand-accent)" : "4px solid transparent"
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                                        {renderItem ? renderItem(item, activeId === item.id) : (
                                                            <div className="text-start overflow-hidden">
                                                                <p className={`mb-0 fw-semibold text-truncate ${activeId === item.id ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '13.5px' }}>
                                                                    {item.name || item.title}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Ícone de Arraste à Direita (Substituindo a seta) */}
                                                    <div {...providedSnapshot.dragHandleProps} className="ms-2 text-muted opacity-50 hover-opacity-100 transition-all">
                                                        <i className={`mdi mdi-drag-vertical font-size-20 ${activeId === item.id ? 'text-primary opacity-100' : ''}`}></i>
                                                    </div>
                                                </ListGroupItem>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.innerRef && provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            );
        }

        return (
            <React.Fragment>
                {filteredItems.map((item) => (
                    <ListGroupItem
                        key={item.id}
                        tag="button"
                        action
                        onClick={() => onSelect(item)}
                        className={`py-3 px-4 d-flex align-items-center justify-content-between transition-all select-none cursor-pointer neo-sidebar-item ${activeId === item.id ? 'active' : ''}`}
                        style={{
                            borderLeft: activeId === item.id ? "4px solid var(--color-brand-accent)" : "4px solid transparent"
                        }}
                    >
                        {renderItem ? renderItem(item, activeId === item.id) : (
                            <div className="text-start overflow-hidden">
                                <p className={`mb-0 fw-semibold text-truncate ${activeId === item.id ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '13.5px' }}>
                                    {item.name || item.title}
                                </p>
                                {item.description && (
                                    <span className="text-muted small text-truncate d-block mt-1" style={{ fontSize: '11px', opacity: 0.8 }}>
                                        {item.description}
                                    </span>
                                )}
                            </div>
                        )}

                    </ListGroupItem>
                ))}
            </React.Fragment>
        );
    };

    return (
        <Card className="neo-master-sidebar overflow-hidden d-flex flex-column" style={{ position: 'sticky', top: '85px', maxHeight: 'calc(100vh - 100px)' }}>
            <CardHeader className="bg-white border-bottom py-3 px-4 flex-shrink-0">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-start overflow-hidden">
                        <div className="me-2 flex-shrink-0 text-primary">
                            <i className={`${icon} font-size-20`}></i>
                        </div>
                        <div className="overflow-hidden">
                            <h6 className="mb-0 fw-bold text-dark text-truncate">{title}</h6>
                            {subtitle && <p className="text-muted small mb-0 text-truncate">{subtitle}</p>}
                        </div>
                    </div>
                    {onCreateNew && (
                        <Button
                            color="primary"
                            size="sm"
                            className="btn-soft-primary px-3 shadow-none border-0 rounded-2 d-flex align-items-center"
                            onClick={onCreateNew}
                        >
                            <i className="mdi mdi-plus font-size-16"></i>
                        </Button>
                    )}
                </div>

                {headerContent && (
                    <div className="mb-2">{headerContent}</div>
                )}

                {!hideSearch && (
                    <div className="search-box position-relative">
                        <Input
                            type="text"
                            className="form-control bg-light border-light rounded-0 font-size-13 py-2 ps-5"
                            placeholder={onReorder ? "Busca desativa ordenação..." : searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="mdi mdi-magnify search-icon position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                    </div>
                )}
            </CardHeader>

            <ListGroup flush className="flex-grow-1 overflow-auto custom-scrollbar">
                {renderListContent()}
            </ListGroup>
        </Card>
    );
};

MasterSidebar.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    items: PropTypes.array.isRequired,
    activeId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onCreateNew: PropTypes.func,
    loading: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    renderItem: PropTypes.func,
    icon: PropTypes.string,
    onReorder: PropTypes.func,
    loadingMessage: PropTypes.string,
    headerContent: PropTypes.node,
    hideSearch: PropTypes.bool,
};

export default MasterSidebar;
